import { useCallback } from 'react';

import {
  FeedbackMessages,
  useFeedback,
} from '@/components/notifications/Feedback/useFeedback';
import { useReportError } from '@/hooks/useReportError';

import { useRegenerateMemoMutation } from '../graphql/RegenerateMemo.generated';

interface RegenerateMemoInput {
  memoId: string;
  specificInstructions?: string;
}

export function useRegenerateMemo() {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();

  const [regenerateMemoMutation, { loading }] = useRegenerateMemoMutation();

  const regenerateMemo = useCallback(
    async ({ memoId, specificInstructions }: RegenerateMemoInput) => {
      try {
        const result = await regenerateMemoMutation({
          variables: {
            input: {
              id: memoId,
              specificInstructions,
            },
          },
          refetchQueries: 'active',
          awaitRefetchQueries: true,
        });

        if (!result.data?.regenerateMemo) {
          throw new Error('Memo regeneration returned no workflow tracker.');
        }

        showFeedback('Memo regeneration started', {
          variant: 'success',
        });

        return result.data.regenerateMemo;
      } catch (error) {
        showFeedback(FeedbackMessages.formSaveError);
        reportError(
          'Could not regenerate memo',
          error instanceof Error
            ? error
            : new Error('Unknown regenerate error'),
          { memoId }
        );
        throw error;
      }
    },
    [regenerateMemoMutation, reportError, showFeedback]
  );

  return {
    regenerateMemo,
    loading,
  };
}
